(function (root, factory) {
  const api = factory();
  // CommonJS is the server/test consumer of this one shared contract. Do not
  // let an ambient browser global replace its authoritative parser.
  if (typeof module === 'object' && module.exports) { module.exports = api; return; }
  const existing = root?.DIMBudget;
  if (existing !== undefined) {
    if (existing?.schemaVersion !== api.schemaVersion || typeof existing.reconcileBudget !== 'function' || !Object.isFrozen(existing)) throw new Error('DIM_BUDGET_CONFLICT');
    return;
  }
  if (root) root.DIMBudget = api;
}(typeof globalThis !== 'undefined' ? globalThis : undefined, function () {
  'use strict';

  const fail = code => { throw new Error(code); };
  const defaultBudgetMinor = value => {
    if (value === null) return null;
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100000
      || Math.abs(value * 100 - Math.round(value * 100)) > 1e-7) fail('INVALID_BUDGET');
    return Math.round(value * 100);
  };
  const clarify = basis => Object.freeze({ status: 'clarification', maxMinor: null, basis });

  function reconcileBudget({ budgetGbp, rawText }, adapters = {}) {
    const toMinor = typeof adapters.budgetMinor === 'function' ? adapters.budgetMinor : defaultBudgetMinor;
    const reject = typeof adapters.fail === 'function' ? adapters.fail : fail;
    const structured = toMinor(budgetGbp);
    const raw = rawText ?? '';
    if (typeof raw !== 'string' || raw.length > 1800) return reject('INVALID_BUDGET_INPUT');
    if (/[$€]|\b(?:USD|EUR|dollars?|euros?)\b/i.test(raw)) return clarify('unsupported_currency');
    const matches = [...raw.matchAll(/(?:£\s*(\d+(?:\.\d{1,2})?)(?!\d|\.\d)|\b(\d+(?:\.\d{1,2})?)\s*(?:pounds?|GBP)\b)/gi)];
    if (!matches.length) {
      if (/£|\b(?:budget|maximum|max|spend|cost|pounds?|GBP)\b/i.test(raw)) return clarify('unparsed_budget');
      return Object.freeze({ status: 'resolved', maxMinor: structured, basis: structured === null ? 'none' : 'structured', priceBasis: 'product-only' });
    }
    if (/\b(?:delivered|all[ -]in)\b|\bincl\.?\s+(?:delivery|shipping|postage)\b|\b(?:including|includes?|with|plus|inc\.?)\s+(?:the\s+)?(?:shipping|delivery|postage)\b|\btotal\b/i.test(raw)) return clarify('delivery_total_needs_quote');
    if (/\b(?:around|roughly|about|approximately|between|maybe|perhaps)\s*(?:£|\d)/i.test(raw) || (matches.length > 1 && /\bor\b/i.test(raw))) return clarify('ambiguous_amount');
    if (/\b(?:not|never)\s+(?:under|below|less than|more than|over|above)\s*(?:£|\d)/i.test(raw)) return clarify('negated_amount');
    const caps = [];
    for (const match of matches) {
      const before = raw.slice(Math.max(0, match.index - 65), match.index);
      const after = raw.slice(match.index + match[0].length, match.index + match[0].length + 25);
      const preceding = /\b(?:maximum|max(?:imum)?\s+budget|budget|spend|no more than|at most|up to|under|below|less than)(?:\s+(?:is|of|a|the|only))?\s*[:=]?\s*$/i;
      if (!preceding.test(before) && !/^\s*(?:max(?:imum)?|budget)\b/i.test(after)) return clarify('amount_not_confirmed_ceiling');
      const stated = toMinor(Number(match[1] || match[2]));
      let cap = stated;
      if (/\b(?:under|below|less than)\s*$/i.test(before)) cap -= 1;
      if (cap < 0) return clarify('invalid_ceiling');
      caps.push({ stated, cap });
    }
    const stated = [...new Set(caps.map(({ stated }) => stated))];
    if (stated.length !== 1 || (structured !== null && structured !== stated[0]
      && !caps.some(({ cap }) => structured === cap))) return clarify('conflicting_amounts');
    return Object.freeze({ status: 'resolved', maxMinor: Math.min(...caps.map(({ cap }) => cap), structured ?? Infinity), basis: 'explicit_ceiling', priceBasis: 'product-only' });
  }

  return Object.freeze({ schemaVersion: 'dim-budget@1', reconcileBudget });
}));
